import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api } from '../lib/api';
import {
  ACCEPTED_ATTACHMENT_TYPES,
  ImageUploadError,
  prepareAttachment,
} from '../lib/image-upload';
import { haptic } from '../lib/telegram';
import type { CustomerOrderDetails } from '../types';
import { Button, Card, ErrorNotice, Field, Input } from './ui';

/**
 * «Я перевёл деньги». Заказчик называет сумму и, если есть, прикладывает чек.
 * Это не платёж: в счёт сумма пойдёт только после того, как художник заглянет
 * в свой банк и подтвердит поступление. Так и написано в подсказке, чтобы
 * никто не считал заказ оплаченным раньше времени.
 */
export function PaymentClaimForm({ order }: { order: CustomerOrderDetails }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [comment, setComment] = useState('');
  const [receipt, setReceipt] = useState<{ name: string; data: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api(`/customer/orders/${order.id}/payment-claims`, {
        method: 'POST',
        body: {
          amount: amount.trim(),
          method: method.trim() || null,
          comment: comment.trim() || null,
          receipt: receipt ? { data: receipt.data, fileName: receipt.name } : null,
        },
      }),
    onSuccess: () => {
      haptic('success');
      setAmount('');
      setMethod('');
      setComment('');
      setReceipt(null);
      void queryClient.invalidateQueries({ queryKey: ['customer', 'order', order.id] });
    },
  });

  const pickReceipt = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;

    setFileError(null);
    try {
      setReceipt({ name: file.name, data: await prepareAttachment(file) });
    } catch (failure) {
      setFileError(
        failure instanceof ImageUploadError ? failure.message : 'Не удалось прочитать файл',
      );
    } finally {
      if (input.current) input.current.value = '';
    }
  };

  return (
    <Card>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack stack--compact"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <Field label="Сколько перевели">
          <Input
            inputMode="decimal"
            value={amount}
            placeholder="5000.00"
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        <Field label="Чем платили" hint="Необязательно">
          <Input
            value={method}
            placeholder="Перевод по номеру телефона"
            onChange={(event) => setMethod(event.target.value)}
          />
        </Field>
        <Field label="Что передать художнику" hint="Необязательно">
          <Input
            value={comment}
            placeholder="Отправил с другой карты"
            onChange={(event) => setComment(event.target.value)}
          />
        </Field>

        {/* Чек необязателен: перевод между людьми не всегда оставляет документ,
            а требовать его — значит закрыть дорогу тем, у кого его просто нет. */}
        <div className="claim-receipt">
          <Button variant="secondary" onClick={() => input.current?.click()}>
            {receipt ? 'Заменить чек' : '+ Приложить чек'}
          </Button>
          {receipt && <span className="meta">{receipt.name}</span>}
          {receipt && (
            <button type="button" className="link-button" onClick={() => setReceipt(null)}>
              Убрать
            </button>
          )}
        </div>
        {fileError && <span className="field__error">{fileError}</span>}

        <input
          ref={input}
          type="file"
          accept={ACCEPTED_ATTACHMENT_TYPES}
          hidden
          onChange={(event) => void pickReceipt(event.target.files)}
        />

        {mutation.error && <ErrorNotice error={mutation.error} />}
        <Button type="submit" disabled={!amount.trim() || mutation.isPending}>
          {mutation.isPending ? 'Отправляем…' : 'Сообщить об оплате'}
        </Button>
        <p className="muted settings-note">
          Художник подтвердит поступление вручную — до этого сумма не зачтётся.
        </p>
      </form>
    </Card>
  );
}
